import React from "react";
import { Skeleton, useTheme, keyframes, Box } from "@mui/material";

// Shimmer animation for skeleton
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function RecipeLoadingSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Dynamic background with subtle gradient - matching RecipeDetail
  const cardBgGradient = isDark
    ? "linear-gradient(135deg, rgba(30, 30, 35, 1) 0%, rgba(20, 20, 25, 1) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)";

  // Skeleton colors
  const skeletonBg = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
  const skeletonHighlight = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  return (
    <Box
      sx={{
        // Outer wrapper for border - matching RecipeDetail
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        padding: "2px",
        borderRadius: "18px",
        mb: 1.5, // Matching RecipeDetail
        boxShadow: `0 2px 12px -4px rgba(0,0,0,${isDark ? "0.3" : "0.08"})`,
      }}
    >
      {/* Inner card */}
      <div
        style={{
          background: cardBgGradient,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: ".75em",
          }}
        >
          {/* Left Column */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            {/* Title Placeholder */}
            <Skeleton 
              variant="text" 
              width="65%" 
              height={24} 
              sx={{ 
                borderRadius: "6px",
                bgcolor: skeletonBg,
                "&::after": {
                  background: `linear-gradient(90deg, transparent, ${skeletonHighlight}, transparent)`,
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                }
              }} 
            />

            {/* Book Name Placeholder */}
            <Skeleton
              variant="text"
              width="40%"
              height={17}
              sx={{ 
                marginTop: "6px",
                borderRadius: "4px",
                bgcolor: skeletonBg,
              }}
            />

            {/* Stats Bar - matching main branch styling */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 5,
              }}
            >
              {/* First Stat: Ingredients - 18x18 icon */}
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Skeleton 
                  variant="circular" 
                  width={18} 
                  height={18} 
                  sx={{ bgcolor: skeletonBg }}
                />
                <Skeleton 
                  variant="text" 
                  width={14} 
                  height={20}
                  sx={{ borderRadius: "3px", bgcolor: skeletonBg }}
                />
              </div>

              {/* Second Stat: Active Time - 20x20 icon */}
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Skeleton 
                  variant="circular" 
                  width={20} 
                  height={20}
                  sx={{ bgcolor: skeletonBg }}
                />
                <Skeleton 
                  variant="text" 
                  width={28} 
                  height={20}
                  sx={{ borderRadius: "3px", bgcolor: skeletonBg }}
                />
              </div>

              {/* Third Stat: Inactive Time - 20x20 icon */}
              <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Skeleton 
                  variant="circular" 
                  width={20} 
                  height={20}
                  sx={{ bgcolor: skeletonBg }}
                />
                <Skeleton 
                  variant="text" 
                  width={24} 
                  height={20}
                  sx={{ borderRadius: "3px", bgcolor: skeletonBg }}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Image */}
          <div
            style={{
              width: "75px",
              height: "75px",
              borderRadius: ".5em",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
              overflow: "hidden",
              flexShrink: 0,
              marginLeft: "1em",
            }}
          >
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height="100%"
              sx={{
                bgcolor: skeletonBg,
                "&::after": {
                  background: `linear-gradient(90deg, transparent, ${skeletonHighlight}, transparent)`,
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                }
              }}
            />
          </div>
        </div>
      </div>
    </Box>
  );
}

export default RecipeLoadingSkeleton;